import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Price } from 'domain/listing/value-objects';
import { LOCALE_COMMON_PATHS, LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';
import type { SupportedCurrency } from 'utils/i18n-format-currency';

export interface UsePriceInfoTooltipOptions {
  amount: number | null;
  currency: SupportedCurrency;
  blur?: boolean;
  secondaryText?: string;
}

export interface UsePriceInfoTooltipResult {
  tooltipText: string;
  secondaryText: string | undefined;
  ariaLabel: string;
  shouldShow: boolean;
}

/**
 * Hook for price info tooltip data derivation.
 * 
 * Determines tooltip text and visibility based on price state.
 */
export function usePriceInfoTooltip({
  amount,
  currency,
  blur = false,
  secondaryText,
}: UsePriceInfoTooltipOptions): UsePriceInfoTooltipResult {
  const { t } = useTranslation(Namespaces.COMMON);

  return useMemo(() => {
    const vo = Price.from(amount);

    // Don't show tooltip if no valid price or blurred
    if (!vo.isPositive || blur) {
      return {
        tooltipText: '',
        secondaryText: undefined,
        ariaLabel: '',
        shouldShow: false,
      };
    }

    const tooltipText = t(LOCALE_LISTING_PATHS.PRICE_TOOLTIP_LISTED_PRICE_IN_CURRENCY, {
      currency,
      defaultValue: `Listed price in listed location currency (${currency}).`,
    });

    const ariaLabel = t(LOCALE_COMMON_PATHS.LISTING_PRICE_INFORMATION, {
      defaultValue: 'Price information',
    });

    return {
      tooltipText,
      secondaryText,
      ariaLabel,
      shouldShow: true,
    };
  }, [amount, currency, blur, secondaryText, t]);
}
